import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios'
import Swal from 'sweetalert2'

import { useAuthStore } from '@/store/auth.store'
import { config } from '@/core/config'
import { extractApiErrorMessage, showError } from '@/lib/swal'

// Extend Axios config types to include tempToken
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  tempToken?: string
  /** When true, HTTP 500 does not open the global error SweetAlert (caller handles the error). */
  skipGlobal500Swal?: boolean
  /**
   * When true, the response error interceptor skips global SweetAlerts for this request (caller shows UI).
   * The only exception is a real session expiry flow on HTTP 401 (expired token logout + redirect).
   */
  skipHandledLocallyErrorUI?: boolean
}

interface CustomInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  tempToken?: string
  skipGlobal500Swal?: boolean
  skipHandledLocallyErrorUI?: boolean
}

// Base API configuration - using centralized config from config.ts
// This ensures single source of truth: .env → config.ts → client.ts
const API_BASE_URL = config.api.baseUrl
const API_TIMEOUT = config.api.timeout

/** Only these may go out without a Bearer access token. */
const PUBLIC_API_PATHS = [
  '/api/ApiAuthentication/Login',
  '/api/ApiAuthentication/ForgetPassword',
]

function isPublicApiPath(url?: string): boolean {
  if (!url) return false
  return PUBLIC_API_PATHS.some((path) => url.includes(path))
}

export function requireAccessToken(): string {
  const { token, isTokenExpired, logout } = useAuthStore.getState()
  if (!token || isTokenExpired()) {
    if (token) logout()
    throw new Error('Access token required')
  }
  return token
}

// Warn only when VITE_API_URL is missing from .env (fallback default is used)
if (!import.meta.env.VITE_API_URL) {
  console.warn('⚠️ VITE_API_URL is not set in .env. Using fallback:', config.api.baseUrl)
  console.warn('⚠️ Create .env with VITE_API_URL=https://your-api-host')
}

/**
 * Axios client instance with interceptors for JWT handling and error management
 * Updated to handle both stored tokens and temporary tokens for specific requests
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Request interceptor: every call except login/forget-password must send a Bearer access token
apiClient.interceptors.request.use(
  (config: CustomInternalAxiosRequestConfig) => {
    const tempToken = config.tempToken

    if (tempToken) {
      config.headers.Authorization = `Bearer ${tempToken}`
      delete config.tempToken
    } else if (!isPublicApiPath(config.url)) {
      try {
        const token = requireAccessToken()
        config.headers.Authorization = `Bearer ${token}`
      } catch {
        return Promise.reject(
          new AxiosError('Access token required', 'ERR_UNAUTHORIZED', config)
        )
      }
    }

    // If data is FormData, remove Content-Type header to let Axios set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    // Log requests in development
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        headers: {
          ...config.headers,
          Authorization: config.headers.Authorization ? '[HIDDEN]' : undefined,
        },
        data: config.data instanceof FormData ? '[FormData]' : config.data,
      })
    }

    return config
  },
  (error) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor: Handle responses and errors globally
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful responses in development
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        data: response.data,
      })
    }

    return response
  },
  async (error: AxiosError) => {
    const { response, config } = error
    const customCfg = config as CustomInternalAxiosRequestConfig | undefined
    const skipLocalUI = Boolean(customCfg?.skipHandledLocallyErrorUI)

    const { logout } = useAuthStore.getState()

    const forceReLogin = () => {
      logout()
      Swal.fire({
        icon: 'error',
        title: 'Session Expired',
        text: 'Please login again.',
        confirmButtonText: 'OK',
        allowOutsideClick: false,
      }).then(() => {
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      })
    }

    const isInvalidAccessToken =
      response?.status === 401 &&
      !isPublicApiPath(config?.url) &&
      !(customCfg?.tempToken)

    // Log errors in development
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.error(`❌ API Error: ${config?.method?.toUpperCase()} ${config?.url}`, {
        status: response?.status,
        data: response?.data,
        message: error.message,
      })
    }

    // Caller-owned UX: skip *all* global SweetAlerts for this request except true session expiry on 401.
    if (skipLocalUI) {
      if (isInvalidAccessToken) {
        forceReLogin()
      } else if (response?.status === 401 && customCfg?.tempToken) {
        console.error('❌ Temporary token invalid or expired')
      }

      return Promise.reject(error)
    }

    const markShownGlobally = () => {
      ;(error as AxiosError & { shownGlobally?: boolean }).shownGlobally = true
    }

    const showResponseError = async (title: string, fallback: string) => {
      const message = extractApiErrorMessage(response?.data) || fallback
      markShownGlobally()
      await showError(title, message)
    }

    // Handle different error scenarios
    switch (response?.status) {
      case 401: {
        if (customCfg?.tempToken) {
          console.error('❌ Temporary token invalid or expired')
        } else if (isInvalidAccessToken) {
          forceReLogin()
        }
        break
      }
      
      case 403:
        markShownGlobally()
        await showError('Access Denied', 'You do not have permission for this action.')
        break
        
      case 404:
        // Do not show global alert for 404 - let the caller handle it (e.g. list pages show empty state without toast/alert)
        break

      case 400:
      case 409:
      case 422:
        await showResponseError('Validation Error', 'One or more validation errors occurred.')
        break
        
      case 500: {
        const skipSwal = (config as CustomInternalAxiosRequestConfig)?.skipGlobal500Swal
        if (!skipSwal) {
          await showResponseError('Server Error', 'Internal server error. Please try again later.')
        }
        break
      }
        
      default:
        if (error.code === 'ECONNABORTED') {
          markShownGlobally()
          await showError('Timeout', 'Request timeout. Please check your connection.')
        } else if (error.code === 'ERR_NETWORK') {
          markShownGlobally()
          await showError('Network Error', 'Network error. Please check your connection.')
        } else if (response) {
          await showResponseError('Error', 'An unexpected error occurred.')
        }
    }

    return Promise.reject(error)
  }
)

/**
 * Helper function to make API calls with a temporary token
 * Useful for calls that need a token before it's stored in the auth store
 */
export const apiClientWithToken = (token: string) => {
  return {
    get: (url: string, config: CustomAxiosRequestConfig = {}) => 
      apiClient.get(url, { ...config, tempToken: token } as CustomAxiosRequestConfig),
    post: (url: string, data?: any, config: CustomAxiosRequestConfig = {}) => 
      apiClient.post(url, data, { ...config, tempToken: token } as CustomAxiosRequestConfig),
    put: (url: string, data?: any, config: CustomAxiosRequestConfig = {}) => 
      apiClient.put(url, data, { ...config, tempToken: token } as CustomAxiosRequestConfig),
    delete: (url: string, config: CustomAxiosRequestConfig = {}) => 
      apiClient.delete(url, { ...config, tempToken: token } as CustomAxiosRequestConfig),
  }
}

export default apiClient 